import { jsx, jsxs } from 'react/jsx-runtime';
import { createContext, useState, useRef, useEffect, useCallback, useContext } from 'react';
import { getNow, getTzTodayDate, getDateUtilsConfig, getTzStartOfYear, getTzStartOfMonth, addMonths, getTzEndOfDay, getTzTimeStates, getTzDate } from '@ninja/utilities';
import { useResponsiveView } from './useResponsiveView.js';
import { useWebUIConfigOptions } from './ThemeProvider2.js';
import Button from './Buttons2.js';
import './Form2.js';
import 'lodash';
import 'clsx';
import 'lodash/includes';
import styles from './DatePicker4.js';
import { CalendarsView } from './components11.js';
import { DaysView } from './components12.js';
import { DaysViewHeader } from './components13.js';
import { IntervalTimeFooter } from './components14.js';
import { MonthsView } from './components15.js';
import { MonthsViewHeader } from './components16.js';
import { TimeFooter } from './components17.js';
import { TimeView } from './components18.js';
import { TimeViewHeader } from './components19.js';
import { YearsView } from './components20.js';
import { YearsViewHeader } from './components21.js';

const calendarContext = createContext({
    viewState: {
        tzDate: getNow(),
        viewMode: 'days',
        viewNavigate: 0,
        currentCalendar: 'gregorian',
        intervalStartDate: undefined,
        intervalEndDate: undefined,
        intervalEndDateHover: undefined,
    },
    updateViewState: () => undefined,
    defaultViewDate: undefined,
    value: undefined,
    onDateSelection: () => undefined,
    onCancelSelection: () => undefined,
    fromDate: undefined,
    toDate: undefined,
    enableTimeSelection: false,
    calendars: ['gregorian'],
    isIntervalPicker: false,
    maxSelectionRangeDays: undefined,
});
const CalendarView = ({ pickerViewMode = 'days', currentDate, currentEndDate, onChange, defaultMonth, fromDate, toDate, enableTimeSelection = false, onCancelSelection, autoSelectTime, calendars, selectedCalendar, onCalendarChange, isIntervalPicker, maxSelectionRangeDays, }) => {
    const stateDate = defaultMonth !== null && defaultMonth !== void 0 ? defaultMonth : getTzTodayDate();
    const [viewState, setViewState] = useState({
        intervalStartDate: currentDate,
        intervalEndDate: currentEndDate,
        intervalEndDateHover: undefined,
        tzDate: stateDate,
        viewMode: pickerViewMode,
        viewNavigate: 0,
        currentCalendar: (selectedCalendar !== null && selectedCalendar !== void 0 ? selectedCalendar : (calendars && calendars.length > 0)) ? calendars[0] : 'gregorian',
    });
    const fromTimeHistory = useRef();
    const toTimeHistory = useRef();
    const { timeFormat } = getDateUtilsConfig();
    const { sm } = useResponsiveView();
    useEffect(() => {
        if (selectedCalendar)
            setViewState((prev) => {
                return Object.assign(Object.assign({}, prev), { currentCalendar: selectedCalendar });
            });
    }, [selectedCalendar]);
    const { isRtlLocale } = useWebUIConfigOptions();
    const saveHistory = () => {
        if (viewState.intervalStartDate) {
            fromTimeHistory.current = getTzTimeStates(viewState.intervalStartDate);
        }
        if (viewState.intervalEndDate) {
            toTimeHistory.current = getTzTimeStates(viewState.intervalEndDate);
        }
    };
    const geRevisedTime = (dateValue, timeState) => {
        var _a;
        if (!timeState)
            return dateValue;
        const hour = (_a = ((timeState === null || timeState === void 0 ? void 0 : timeState.hours) && timeFormat === '12hr' && (timeState === null || timeState === void 0 ? void 0 : timeState.hours) > 12 ? timeState.hours - 12 : timeState.hours)) !== null && _a !== void 0 ? _a : 0;
        const minutes = timeState.minutes;
        const valueTimeState = getTzTimeStates(dateValue);
        const resultTzDate = getTzDate({
            year: valueTimeState.year,
            month: valueTimeState.month,
            day: valueTimeState.day,
            hours: hour,
            minutes: minutes,
        });
        return resultTzDate;
    };
    /**
     * Handles the selection of dates in an interval picker.
     *
     * - 1st selection is the start date.
     * - 2nd selection if greater than start date is the end date else the selections are reversed
     * - 3rd selection resets date and performs 1st selection
     *
     * @param {Date} selected - The selected date.
     */
    const handleIntervalSelection = (selected) => {
        const { intervalStartDate: currentFromDate, intervalEndDate: currentToDate } = viewState;
        if (!currentFromDate) {
            setViewState((prev) => (Object.assign(Object.assign({}, prev), { intervalStartDate: geRevisedTime(selected, fromTimeHistory.current) })));
        }
        else if (currentFromDate && !currentToDate) {
            if (selected >= currentFromDate)
                setViewState((prev) => (Object.assign(Object.assign({}, prev), { intervalEndDate: toTimeHistory.current
                        ? geRevisedTime(selected, toTimeHistory.current)
                        : getTzEndOfDay(selected) })));
            else
                setViewState((prev) => {
                    var _a, _b;
                    return (Object.assign(Object.assign({}, prev), { intervalEndDate: toTimeHistory.current
                            ? geRevisedTime((_a = prev.intervalStartDate) !== null && _a !== void 0 ? _a : getNow(), toTimeHistory.current)
                            : getTzEndOfDay((_b = prev.intervalStartDate) !== null && _b !== void 0 ? _b : getNow()), intervalStartDate: geRevisedTime(selected, fromTimeHistory.current) }));
                });
        }
        else {
            saveHistory();
            setViewState((prev) => {
                var _a;
                return (Object.assign(Object.assign({}, prev), { intervalStartDate: geRevisedTime(selected, getTzTimeStates((_a = viewState.intervalStartDate) !== null && _a !== void 0 ? _a : getNow())), intervalEndDate: undefined }));
            });
        }
    };
    const onDateSelection = useCallback((selectedDate) => {
        switch (viewState.viewMode) {
            case 'time':
                onChange(selectedDate, viewState.currentCalendar);
                break;
            case 'days':
                if (!isIntervalPicker) {
                    if (enableTimeSelection && !autoSelectTime) {
                        setViewState((prev) => (Object.assign(Object.assign({}, prev), { tzDate: selectedDate, viewMode: 'time' })));
                    }
                    else {
                        onChange(selectedDate, viewState.currentCalendar);
                    }
                }
                else {
                    handleIntervalSelection(selectedDate);
                }
                break;
            case 'months':
                if (pickerViewMode === 'months') {
                    onChange(getTzStartOfMonth(selectedDate, viewState.currentCalendar), viewState.currentCalendar);
                }
                else {
                    setViewState((prev) => (Object.assign(Object.assign({}, prev), { tzDate: selectedDate, viewMode: 'days' })));
                }
                break;
            case 'years':
                if (pickerViewMode === 'years') {
                    onChange(getTzStartOfYear(selectedDate, viewState.currentCalendar), viewState.currentCalendar);
                }
                else {
                    setViewState((prev) => (Object.assign(Object.assign({}, prev), { tzDate: selectedDate, viewMode: 'months' })));
                }
                break;
        }
    }, [
        pickerViewMode,
        viewState.viewMode,
        viewState.currentCalendar,
        viewState.intervalStartDate,
        viewState.intervalEndDate,
    ]);
    const calendarHeaderSection = {
        years: jsx(YearsViewHeader, {}),
        months: jsx(MonthsViewHeader, {}),
        days: jsx(DaysViewHeader, {}),
        time: pickerViewMode === 'time' ? null : jsx(TimeViewHeader, {}),
    }[viewState.viewMode];
    const nextMonthViewState = Object.assign(Object.assign({}, viewState), { tzDate: addMonths(viewState.tzDate, 1) });
    const updateViewState = (newPartialState) => {
        setViewState((prev) => (Object.assign(Object.assign({}, prev), newPartialState)));
    };
    const calendarView = {
        days: isIntervalPicker ? (jsxs("div", { className: "flex flex-row gap-8 w-full", children: [jsx(DaysView, { onClick: onDateSelection, viewState: viewState, value: viewState.intervalStartDate, secondaryValue: viewState.intervalEndDate, updateViewState: updateViewState }), !sm && (jsx(DaysView, { onClick: onDateSelection, viewState: nextMonthViewState, value: viewState.intervalStartDate, secondaryValue: viewState.intervalEndDate, updateViewState: updateViewState }))] })) : (jsx(DaysView, { onClick: onDateSelection, viewState: viewState, value: viewState.intervalStartDate, secondaryValue: undefined })),
        months: jsx(MonthsView, { onClick: onDateSelection }),
        years: jsx(YearsView, { onClick: onDateSelection }),
        time: jsx(TimeView, { currentSelectedDate: viewState === null || viewState === void 0 ? void 0 : viewState.tzDate }),
    }[viewState.viewMode];
    return (jsx(calendarContext.Provider, { value: {
            viewState,
            value: viewState.intervalStartDate,
            fromDate,
            toDate,
            defaultViewDate: defaultMonth,
            updateViewState: (newPartialState) => {
                setViewState((prev) => (Object.assign(Object.assign({}, prev), newPartialState)));
            },
            onDateSelection: (date) => {
                onDateSelection(date);
            },
            onCancelSelection,
            enableTimeSelection,
            calendars,
            onCalendarChange,
            isIntervalPicker,
            maxSelectionRangeDays,
        }, children: jsxs("div", { className: styles.mainViewContainer(isIntervalPicker), children: [calendars.length > 1 && (jsx("div", { className: styles.headerContainer(isRtlLocale, viewState.viewMode), children: jsx(CalendarsView, {}) })), pickerViewMode !== 'time' && (jsx("div", { className: styles.headerContainer(isRtlLocale, viewState.viewMode), children: calendarHeaderSection })), jsx("div", { className: styles.viewContainer(pickerViewMode), children: calendarView }), enableTimeSelection && viewState.viewMode !== 'time' && !isIntervalPicker && (jsx("div", { className: styles.viewTimeFooter, children: jsx(TimeFooter, {}) })), enableTimeSelection && isIntervalPicker && viewState.viewMode !== 'time' && (jsxs("div", { className: "flex flex-col md:flex-row", children: [jsx(IntervalTimeFooter, { label: isRtlLocale ? 'وقت البدء:' : 'Start Time:', fieldKey: "intervalStartDate" }), jsx(IntervalTimeFooter, { label: isRtlLocale ? 'وقت الإنتهاء:' : 'End Time:', fieldKey: "intervalEndDate" })] })), isIntervalPicker && (jsxs("div", { className: styles.viewTimeFooter, children: [jsx(Button, { variant: "secondary", onClick: onCancelSelection, size: "small", children: isRtlLocale ? 'إلغاء' : 'Cancel' }), jsx(Button, { onClick: () => {
                                if (viewState.intervalStartDate)
                                    onChange(viewState.intervalStartDate, viewState.currentCalendar, viewState.intervalEndDate);
                            }, 
                            // start date mandatory
                            disabled: !viewState.intervalStartDate, size: "small", children: isRtlLocale ? 'موافق' : 'Apply' })] }))] }) }));
};
const useCalendarViewContext = () => useContext(calendarContext);

export { CalendarView, useCalendarViewContext };
